import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import { TrustList } from "../components/TrustList";
import { Flame, Wrench, Zap, ShieldCheck, CheckCircle2 } from "lucide-react";

import { pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const boilerSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Boiler Servicing, Repairs & Installations",
  provider: { "@type": "LocalBusiness", name: "TOFT Gas, Heating & Plumbing", telephone: "+447885592820" },
  areaServed: ["Rhondda Cynon Taf", "Cardiff", "Bridgend", "Caerphilly"],
  url: `${SITE_URL}/boiler-services`,
  offers: { "@type": "Offer", priceCurrency: "GBP", price: "70", description: "Boiler servicing from £70" },
};

const BoilerServicesPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.boiler} jsonLd={boilerSchema} />
      <PageHero
        eyebrow="Boiler Services"
        image={pageHeroImages.boiler}
        title="Boiler Servicing, Repairs & Installations from £70 Across Cardiff, Pontypridd, Bridgend &amp; Caerphilly"
        subtitle="Annual boiler servicing from £70, fault diagnosis and free quotations for new boiler installations. Gas Safe registered engineer providing boiler servicing in Cardiff, boiler repairs in Pontypridd, boiler installations across Bridgend, Caerphilly and Rhondda Cynon Taf."
      />

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* Boiler servicing */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="h-14 w-14 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center mb-4">
                <Flame className="h-7 w-7" />
              </div>
              <p className="text-sm font-bold tracking-widest uppercase text-brand-blue">
                From £70
              </p>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mt-2">
                Boiler Servicing
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed">
              <p>
                Annual boiler servicing helps keep your heating system operating safely,
                efficiently and reliably.
              </p>
              <p>
                A regular boiler service may help reduce the risk of breakdowns, identify
                issues early and is often required to maintain manufacturer warranties.
              </p>
              <p className="font-semibold text-brand-navy">
                Boiler servicing starts from £70 for most combi boilers.
              </p>
            </div>
          </div>

          {/* Boiler repairs */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="h-14 w-14 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center mb-4">
                <Wrench className="h-7 w-7" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Boiler Repairs
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed">
              <p>
                No heating or hot water can quickly become stressful for homeowners and
                landlords alike.
              </p>
              <p>
                We provide fault diagnosis and repairs for many common boiler problems and
                will always aim to find the most practical and cost-effective solution.
              </p>
              <ul className="grid sm:grid-cols-2 gap-2 pt-2">
                {["No heating", "No hot water", "Fault codes", "Low pressure", "Leaks", "Thermostat faults", "Heating performance issues"].map(
                  (i) => (
                    <li key={i} className="flex items-start gap-2 text-base">
                      <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                      <span>{i}</span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>

          {/* Boiler installations */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="h-14 w-14 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center mb-4">
                <Zap className="h-7 w-7" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Boiler Installations
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed">
              <p>
                Sometimes a boiler becomes unreliable, inefficient or simply too expensive
                to keep repairing.
              </p>
              <p>When that happens, we can provide a free quotation for a replacement.</p>
              <p className="font-semibold text-brand-navy">Benefits of a modern boiler may include:</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {["Improved reliability", "Better efficiency", "Lower running costs", "Longer warranties", "Fewer tenant callouts"].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-base">
                    <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              <p className="pt-2 italic text-slate-600">
                We'll always explain your options clearly and honestly. No pressure. No
                hard selling. Just practical advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-7">
            <ShieldCheck className="h-7 w-7 text-brand-blue" />
            <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
              Why Choose TOFT?
            </h2>
          </div>
          <TrustList
            items={[
              "Gas Safe Registered",
              "Boiler Servicing From £70",
              "Honest Advice",
              "Reliable Appointments",
              "Free Replacement Quotations",
              "Friendly Service",
              "Trusted By Homeowners & Landlords",
              "Easy Contact By Phone, WhatsApp & Text",
            ]}
            columns={2}
          />
        </div>
      </section>

      <FinalCTA
        title="Need help with your boiler?"
        lines={[
          "Need a boiler service?",
          "Boiler not working properly?",
          "Thinking about replacing your boiler?",
        ]}
      />
    </>
  );
};

export default BoilerServicesPage;
